import ContactReasonsAPI from '../../api/contactReasons';
import types from '../mutation-types';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CONTACT_REASON_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

function findContactReason(records, path) {
  let currentContactReason = records;
  let targetContactReason = null;
  path.forEach(id => {
    if (targetContactReason === null) {
      currentContactReason = currentContactReason.find(
        record => record.id === Number(id)
      );
    } else {
      currentContactReason = currentContactReason.child_reasons.find(
        record => record.id === Number(id)
      );
    }
    targetContactReason = currentContactReason;
  });
  return targetContactReason;
}

export const getters = {
  getContactReasons(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getContactReasonByPath: _state => path => {
    return findContactReason(_state.records, path);
  },
};

export const actions = {
  create: async function createContactReason({ commit }, contactReasonObj) {
    commit(types.SET_CONTACT_REASON_UI_FLAG, { isCreating: true });
    try {
      const { contactReasonPath, data } = contactReasonObj;
      const response = await ContactReasonsAPI.create(data);
      const contactReason = response.data.payload;
      AnalyticsHelper.track(CONTACT_REASON_EVENTS.CREATE);
      commit(types.ADD_CONTACT_REASON, { contactReasonPath, contactReason });
      return contactReason;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_REASON_UI_FLAG, { isCreating: false });
    }
  },
  get: async function getContactReasons({ commit }) {
    commit(types.SET_CONTACT_REASON_UI_FLAG, { isFetching: true });
    try {
      const response = await ContactReasonsAPI.get();
      commit(types.SET_CONTACT_REASONS, response.data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CONTACT_REASON_UI_FLAG, { isFetching: false });
    }
  },
  update: async function updateContactReason({ commit }, contactReasonObj) {
    commit(types.SET_CONTACT_REASON_UI_FLAG, { isUpdating: true });
    try {
      const { contactReasonPath, data } = contactReasonObj;
      const response = await ContactReasonsAPI.update(data.id, data);
      const contactReason = response.data.payload;
      AnalyticsHelper.track(CONTACT_REASON_EVENTS.UPDATE);
      commit(types.EDIT_CONTACT_REASON, { contactReasonPath, contactReason });
      return contactReason;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_REASON_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async function deleteContactReason({ commit }, contactReasonObj) {
    commit(types.SET_CONTACT_REASON_UI_FLAG, { isDeleting: true });
    try {
      const { contactReasonPath, data } = contactReasonObj;
      await ContactReasonsAPI.delete(data.id);
      AnalyticsHelper.track(CONTACT_REASON_EVENTS.DELETED);
      commit(types.DELETE_CONTACT_REASON, {
        contactReasonPath,
        contactReason: data,
      });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_REASON_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_CONTACT_REASON_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_CONTACT_REASONS]: (_state, contactReasons) => {
    _state.records = contactReasons;
  },
  [types.ADD_CONTACT_REASON]: (
    _state,
    { contactReasonPath, contactReason }
  ) => {
    const targetContactReason = findContactReason(
      _state.records,
      contactReasonPath
    );
    if (targetContactReason) {
      targetContactReason.child_reasons.push(contactReason);
    } else {
      _state.records.push(contactReason);
    }
  },
  [types.EDIT_CONTACT_REASON]: (
    _state,
    { contactReasonPath, contactReason }
  ) => {
    let targetContactReason = findContactReason(
      _state.records,
      contactReasonPath
    );
    if (targetContactReason) {
      Object.keys(contactReason).forEach(key => {
        targetContactReason[key] = contactReason[key];
      });
    }
  },
  [types.DELETE_CONTACT_REASON]: (
    _state,
    { contactReasonPath, contactReason }
  ) => {
    const parentPath = contactReasonPath.slice(0, -1);
    const targetArray =
      parentPath.length === 0
        ? _state.records
        : findContactReason(_state.records, parentPath)?.child_reasons;

    if (targetArray) {
      const index = targetArray.findIndex(
        record => record.id === contactReason.id
      );
      if (index !== -1) {
        targetArray.splice(index, 1);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
